import React, { useEffect, useMemo, useState } from 'react';
import { Box, Button, Chip, Paper, Typography, useTheme } from '@mui/material';
import TimerOutlinedIcon from '@mui/icons-material/TimerOutlined';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import { useNavigate } from 'react-router-dom';
import useCachedQuery from '../hooks/useCachedQuery';
import { fetchContestsList, queryKeys, STALE_TIMES } from '../api/queries';

const pad = (n) => String(Math.max(0, n)).padStart(2, '0');

const splitMs = (ms) => {
  const total = Math.max(0, ms);
  const days = Math.floor(total / (1000 * 60 * 60 * 24));
  const hours = Math.floor((total % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutes = Math.floor((total % (1000 * 60)) / (1000 * 60));
  const seconds = Math.floor((total % (1000 * 60)) / 1000);
  return { days, hours, minutes, seconds };
};

const TimeCell = ({ value, label, dark, compact, live }) => (
  <Box
    sx={{
      minWidth: compact ? { xs: 40, sm: 48 } : { xs: 52, sm: 64 },
      px: compact ? 0.5 : 1,
      py: compact ? 0.5 : 0.75,
      borderRadius: '8px',
      textAlign: 'center',
      backgroundColor: live
        ? dark
          ? 'rgba(245,0,87,0.14)'
          : 'rgba(245,0,87,0.08)'
        : dark
          ? 'rgba(255,255,255,0.08)'
          : 'rgba(19,114,116,0.08)',
      border: '1px solid',
      borderColor: live
        ? dark
          ? 'rgba(245,0,87,0.35)'
          : 'rgba(245,0,87,0.25)'
        : dark
          ? 'rgba(255,255,255,0.12)'
          : 'rgba(19,114,116,0.18)',
    }}
  >
    <Typography
      sx={{
        fontWeight: 700,
        fontSize: compact ? { xs: '0.9rem', sm: '1.05rem' } : { xs: '1.1rem', sm: '1.35rem' },
        lineHeight: 1.1,
        fontVariantNumeric: 'tabular-nums',
        color: live ? (dark ? '#ff8ab0' : '#c2185b') : dark ? '#e8f5f5' : '#0b4f51',
      }}
    >
      {pad(value)}
    </Typography>
    <Typography
      sx={{
        fontSize: compact ? '0.55rem' : '0.65rem',
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        color: dark ? 'rgba(255,255,255,0.55)' : 'rgba(11,79,81,0.7)',
        mt: 0.15,
      }}
    >
      {label}
    </Typography>
  </Box>
);

const pickFeaturedContest = (list, nowMs) => {
  const contests = Array.isArray(list) ? list : [];
  const live = contests
    .filter((c) => {
      if (!c?.startTime || !c?.endTime) return false;
      const start = new Date(c.startTime).getTime();
      const end = new Date(c.endTime).getTime();
      return start <= nowMs && nowMs <= end;
    })
    .sort((a, b) => new Date(a.endTime) - new Date(b.endTime));

  if (live[0]) return { contest: live[0], phase: 'live' };

  const upcoming = contests
    .filter((c) => c?.startTime && new Date(c.startTime).getTime() > nowMs)
    .sort((a, b) => new Date(a.startTime) - new Date(b.startTime));

  if (upcoming[0]) return { contest: upcoming[0], phase: 'upcoming' };
  return { contest: null, phase: 'none' };
};

/**
 * Home / promo contest card — prefers a live contest, else the next upcoming one.
 */
const UpcomingContestCountdown = ({ compact = false }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const dark = theme.palette.mode === 'dark';
  const { data: contestsData, loading: contestsLoading } = useCachedQuery(
    queryKeys.contests,
    fetchContestsList,
    { staleTime: STALE_TIMES.contests },
  );
  const loaded = !contestsLoading || Boolean(contestsData);
  const contests = Array.isArray(contestsData) ? contestsData : [];
  const [contest, setContest] = useState(null);
  const [phase, setPhase] = useState('none'); // live | upcoming | none
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    if (!contest && contests.length === 0) return undefined;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [contest, contests.length]);

  // Keep phase in sync; when a live contest ends, promote the next upcoming
  useEffect(() => {
    if (!contests.length) return;
    const picked = pickFeaturedContest(contests, now);
    if (picked.contest?.id !== contest?.id || picked.phase !== phase) {
      setContest(picked.contest);
      setPhase(picked.phase);
    }
  }, [contests, now, contest?.id, phase]);

  const remainingMs = useMemo(() => {
    if (!contest) return 0;
    if (phase === 'live' && contest.endTime) {
      return new Date(contest.endTime).getTime() - now;
    }
    if (contest.startTime) {
      return new Date(contest.startTime).getTime() - now;
    }
    return 0;
  }, [contest, now, phase]);

  const openContest = (e) => {
    e?.stopPropagation?.();
    if (!contest?.id) {
      navigate('/contests');
      return;
    }
    if (phase === 'live') {
      navigate(`/contests/${contest.id}/live`);
      return;
    }
    navigate(`/contests/details/${contest.id}`);
  };

  if (!loaded) {
    return (
      <Paper
        sx={{
          p: { xs: 1, md: 1.5 },
          borderRadius: '12px',
          width: '100%',
          minHeight: compact ? 88 : 72,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: dark ? '#333' : '#f5fafa',
          border: '1px solid',
          borderColor: dark ? 'rgba(255,255,255,0.08)' : 'rgba(19,114,116,0.12)',
        }}
      >
        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
          Checking contests…
        </Typography>
      </Paper>
    );
  }

  if (!contest || phase === 'none' || remainingMs <= 0) {
    return (
      <Paper
        onClick={() => navigate('/contests')}
        sx={{
          p: { xs: 1, md: 1.5 },
          display: 'flex',
          alignItems: 'center',
          gap: { xs: 1.5, md: 2 },
          borderRadius: '12px',
          cursor: 'pointer',
          background: dark
            ? 'linear-gradient(120deg, #1a2e2f 0%, #243b3c 100%)'
            : 'linear-gradient(120deg, #e8f6f6 0%, #f4fbfb 100%)',
          border: '1px solid',
          borderColor: dark ? 'rgba(110, 200, 200, 0.2)' : 'rgba(19,114,116,0.2)',
          width: '100%',
          height: '100%',
          '&:hover': { boxShadow: '0 6px 18px rgba(0, 0, 0, 0.12)' },
        }}
      >
        <TimerOutlinedIcon sx={{ color: dark ? '#7ed4d4' : '#137274', fontSize: { xs: 32, md: 42 } }} />
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Typography
            sx={{
              fontWeight: 'bold',
              fontSize: { xs: '12px', md: '16px' },
              color: dark ? '#fff' : '#0b4f51',
            }}
          >
            Coding Contests
          </Typography>
          <Typography
            sx={{
              fontSize: { xs: '10px', md: '11px' },
              color: dark ? 'lightgrey' : '#424242',
            }}
          >
            No contest live or upcoming right now. Browse all contests.
          </Typography>
        </Box>
        <Button
          size="small"
          variant="contained"
          onClick={(e) => {
            e.stopPropagation();
            navigate('/contests');
          }}
          sx={{
            textTransform: 'none',
            backgroundColor: '#137274',
            '&:hover': { backgroundColor: '#0f5c5e' },
            fontSize: { xs: '11px', md: '12px' },
          }}
        >
          View
        </Button>
      </Paper>
    );
  }

  const isLive = phase === 'live';
  const { days, hours, minutes, seconds } = splitMs(remainingMs);

  return (
    <Paper
      elevation={0}
      onClick={openContest}
      sx={{
        p: { xs: 1, md: 1.5 },
        borderRadius: '12px',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        gap: 1,
        cursor: 'pointer',
        background: isLive
          ? dark
            ? 'linear-gradient(120deg, #2a1520 0%, #3a1c28 55%, #24141c 100%)'
            : 'linear-gradient(120deg, #fff0f5 0%, #ffe8f0 50%, #fff5f8 100%)'
          : dark
            ? 'linear-gradient(120deg, #1a2e2f 0%, #243b3c 55%, #1e3334 100%)'
            : 'linear-gradient(120deg, #e8f6f6 0%, #f4fbfb 50%, #e3f2f2 100%)',
        border: '1px solid',
        borderColor: isLive
          ? dark
            ? 'rgba(245,0,87,0.35)'
            : 'rgba(245,0,87,0.28)'
          : dark
            ? 'rgba(110, 200, 200, 0.2)'
            : 'rgba(19,114,116,0.2)',
        width: '100%',
        height: '100%',
        position: 'relative',
        overflow: 'hidden',
        transition: 'box-shadow 0.3s ease-in-out',
        '&:hover': { boxShadow: '0 6px 18px rgba(0, 0, 0, 0.12)' },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25, minWidth: 0 }}>
        <Box
          sx={{
            width: compact ? 36 : 42,
            height: compact ? 36 : 42,
            borderRadius: '10px',
            display: 'grid',
            placeItems: 'center',
            flexShrink: 0,
            backgroundColor: isLive
              ? dark
                ? 'rgba(245,0,87,0.2)'
                : 'rgba(245,0,87,0.12)'
              : dark
                ? 'rgba(110,200,200,0.15)'
                : 'rgba(19,114,116,0.12)',
            color: isLive ? '#f50057' : dark ? '#7ed4d4' : '#137274',
          }}
        >
          <TimerOutlinedIcon fontSize={compact ? 'small' : 'medium'} />
        </Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          {isLive ? (
            <Chip
              icon={
                <FiberManualRecordIcon
                  sx={{
                    fontSize: '10px !important',
                    color: '#fff !important',
                    animation: 'ch-live-pulse 1.4s ease-in-out infinite',
                    '@keyframes ch-live-pulse': {
                      '0%, 100%': { opacity: 1 },
                      '50%': { opacity: 0.35 },
                    },
                  }}
                />
              }
              label="Live now"
              size="small"
              sx={{
                height: 22,
                mb: 0.35,
                fontWeight: 800,
                fontSize: '0.65rem',
                letterSpacing: '0.04em',
                textTransform: 'uppercase',
                bgcolor: '#f50057',
                color: '#fff',
                '& .MuiChip-icon': { ml: 0.6 },
              }}
            />
          ) : (
            <Typography
              sx={{
                fontSize: '0.65rem',
                fontWeight: 700,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                color: dark ? '#7ed4d4' : '#137274',
              }}
            >
              Upcoming Contest
            </Typography>
          )}
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: { xs: '12px', md: '15px' },
              color: dark ? '#f3fafa' : '#123738',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {contest.title || 'Coding Contest'}
          </Typography>
          {isLive && (
            <Typography
              sx={{
                fontSize: '0.68rem',
                color: dark ? 'rgba(255,255,255,0.55)' : '#6b7280',
                mt: 0.15,
              }}
            >
              Ends in
            </Typography>
          )}
        </Box>
        <Button
          variant="contained"
          size="small"
          onClick={openContest}
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            borderRadius: '8px',
            px: 1.5,
            flexShrink: 0,
            backgroundColor: isLive ? '#f50057' : '#137274',
            '&:hover': { backgroundColor: isLive ? '#c40048' : '#0f5c5e' },
            fontSize: { xs: '11px', md: '12px' },
          }}
        >
          {isLive ? 'Enter' : 'View'}
        </Button>
      </Box>

      <Box sx={{ display: 'flex', gap: 0.6, flexWrap: 'wrap' }}>
        <TimeCell value={days} label="Days" dark={dark} compact={compact} live={isLive} />
        <TimeCell value={hours} label="Hrs" dark={dark} compact={compact} live={isLive} />
        <TimeCell value={minutes} label="Min" dark={dark} compact={compact} live={isLive} />
        <TimeCell value={seconds} label="Sec" dark={dark} compact={compact} live={isLive} />
      </Box>
    </Paper>
  );
};

export default UpcomingContestCountdown;
